import React from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { composeQA } from "@multiweb/reactUtils";
import { IMenuItemData, MenuItemType } from "@multiweb/types/contracts";
import { IAppState, QAProps } from "@multiweb/types/react";
import { createMenuItemActivatedAction } from "../../actions";
import { isActionItemFocused as isActionItemFocusedSelector, isFormItemFocused, NavigationSection } from "../../navigation";
import {
    areFormItemWindowActionsEnabled,
    getFormItem,
    getFormItemCode,
    getFormItemIconCode,
    getFormItemTitle,
    isMenuItemActive,
} from "../../selectors";
import { MenuItemActivatedType } from "../../types";
import { createMenuItemKeyActivationHandler, createMenuItemMouseActivationHandler, createOpenableInTabItemMouseActivationHandler } from "../../utils";
import { FormItemRightPartComponent } from "../FormItemRightPartComponent";
import { MenuItemComponent, MenuItemComponentProps } from "../MenuItemComponent";
import { SubmenuRightPartComponent } from "../SubmenuRightPartComponent";

export type MenuItemProps = {
    /** Id formuláře. */
    formItemId: number;
    /** Sekce, ve které je položka vykreslená. Slouží pro určování zaměření. */
    section: NavigationSection;
    /** Příznak umožňující povolit/zakázat zobrazení kontextové akce pro otevření. Výchozí hodnota je true. */
    openActionEnabled?: boolean;
} & QAProps;

type DispatchProps = {
    menuItemActivated: typeof createMenuItemActivatedAction;
};

type StateProps = {
    formItem?: IMenuItemData;
    formCode?: string;
    windowActionsEnabled: boolean;
    isActionItemFocused: boolean;
} & Pick<MenuItemComponentProps, "iconCode" | "label" | "isFocused">
  & Required<Pick<MenuItemComponentProps, "isActive">>;

const mapStateToProps: MapStateToProps<StateProps, MenuItemProps, IAppState> = (state, { formItemId, section }) => ({
    formItem: getFormItem(state, formItemId),
    formCode: getFormItemCode(state, formItemId),
    iconCode: getFormItemIconCode(state, formItemId),
    label: getFormItemTitle(state, formItemId),
    windowActionsEnabled: areFormItemWindowActionsEnabled(state, formItemId),
    isActive: isMenuItemActive(state, formItemId),
    isFocused: isFormItemFocused(state, { formItemId, section }),
    isActionItemFocused: isActionItemFocusedSelector(state, { parentItemId: formItemId, section }),
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, MenuItemProps> = ({
    menuItemActivated: createMenuItemActivatedAction,
});

const createRightPartComponent = (
    isActive: boolean,
    windowActionsEnabled: boolean,
    isActionItemFocused: boolean,
    openActionEnabled: boolean,
    section: NavigationSection,
    formItem?: IMenuItemData,
) => {
    if (formItem) {
        if (formItem.type === MenuItemType.form) {
            return <FormItemRightPartComponent formItemId={formItem.id} {...{ isActive, windowActionsEnabled, isActionItemFocused, openActionEnabled, section }} />;
        } else if (formItem.type === MenuItemType.folder) {
            return <SubmenuRightPartComponent isActive={isActive} />;
        }
    }

    return undefined;
};

const mergeProps: MergeProps<StateProps, DispatchProps, MenuItemProps, MenuItemComponentProps> = (
    { formItem, formCode, label, iconCode, windowActionsEnabled, isFocused, isActionItemFocused, isActive },
    { menuItemActivated },
    { formItemId, openActionEnabled = true, qa, section },
) => {
    const isFolder = formItem?.type === MenuItemType.folder;
    const menuItemActivatedType = isFolder ? MenuItemActivatedType.formFolder : MenuItemActivatedType.form;
    return {
        label,
        iconCode,
        onClick: isFolder
            ? createMenuItemMouseActivationHandler(formItemId, menuItemActivatedType, section, menuItemActivated)
            : createOpenableInTabItemMouseActivationHandler(formItemId, menuItemActivatedType, section, menuItemActivated),
        onKeyUp: createMenuItemKeyActivationHandler(formItemId, menuItemActivatedType, section, menuItemActivated),
        rightPartComponentCreator: (isActive) => createRightPartComponent(isActive, windowActionsEnabled, isActionItemFocused, openActionEnabled, section, formItem),
        itemId: formItemId,
        qa: composeQA(composeQA(formItemId, formCode), qa),
        isFocused,
        isActive,
    };
};

/**
 * Získá kód ikony a přeložený název pro formulář se zadaným id. Vybere onClick akci a pravé kontextové menu, podle toho jestli se
 * jedná o listový formulář nebo submenu.
 */
export const MenuItem = connect(mapStateToProps, mapDispatchToProps, mergeProps)(MenuItemComponent);
